import { useEffect, useState } from "react";
import api, { fmtPKR, fmtDateTime, downloadFile } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Trash2, Download, Printer, FileDown, Share2, Pencil, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { downloadInvoicePdf, openInvoicePrint, openWhatsAppShare } from "../lib/invoice";
import PageShell from "../components/PageShell";

export default function Reports() {
  const [tab, setTab] = useState("sales");

  // sales
  const [range, setRange] = useState("monthly");
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [salesData, setSalesData] = useState({ sales: [], total: 0, count: 0 });

  // profit
  const [profitRange, setProfitRange] = useState("monthly");
  const [profitData, setProfitData] = useState({ rows: [], total_revenue: 0, total_cost: 0, total_profit: 0 });

  // inventory
  const [invData, setInvData] = useState(null);
  // low stock
  const [lowData, setLowData] = useState(null);

  // settings (for share)
  const [settings, setSettings] = useState(null);

  // edit sale dialog
  const [editSale, setEditSale] = useState(null);   // full sale being edited
  const [editBusy, setEditBusy] = useState(false);

  const loadSales = async () => {
    const params = range === "custom" ? { range_type: "custom", date_from: from, date_to: to } : { range_type: range };
    const res = await api.get("/reports/sales", { params });
    setSalesData(res.data);
  };

  const loadProfit = async () => {
    const params = profitRange === "custom" ? { range_type: "custom", date_from: from, date_to: to } : { range_type: profitRange };
    const res = await api.get("/reports/profit", { params });
    setProfitData(res.data);
  };

  const loadInventory = async () => {
    const res = await api.get("/reports/inventory");
    setInvData(res.data);
  };
  const loadLow = async () => {
    const res = await api.get("/reports/low-stock");
    setLowData(res.data);
  };

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  useEffect(() => {
    if (tab === "sales") loadSales();
    else if (tab === "profit") loadProfit();
    else if (tab === "inventory") loadInventory();
    else if (tab === "lowstock") loadLow();
    // eslint-disable-next-line
  }, [tab, range, from, to, profitRange]);

  const deleteSale = async (s) => {
    if (!window.confirm(`Delete invoice ${s.invoice_no}? Stock and ledger will be restored.`)) return;
    try {
      await api.delete(`/sales/${s.id}`);
      toast.success("Sale deleted, stock restored");
      loadSales();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const reprint = (s) => openInvoicePrint(s.id);
  const downloadPdf = (s) => downloadInvoicePdf(s.id, s.invoice_no);
  const shareWa = async (s) => {
    // Fetch full sale and party phone
    try {
      const full = (await api.get(`/sales/${s.id}`)).data;
      let phone = "";
      if (full.party_id) {
        const p = await api.get(`/parties`).then((r) => r.data.find((x) => x.id === full.party_id));
        phone = p?.phone || "";
      }
      openWhatsAppShare(full, settings, phone);
    } catch {
      toast.error("Unable to prepare WhatsApp share");
    }
  };

  const openEdit = async (s) => {
    try {
      const full = (await api.get(`/sales/${s.id}`)).data;
      setEditSale({
        ...full,
        items: (full.items || []).map((it) => ({ ...it })),
      });
    } catch (err) {
      toast.error("Failed to load invoice");
    }
  };

  const editUpdateItem = (idx, field, value) => {
    setEditSale((es) => {
      const items = es.items.map((it, i) =>
        i === idx ? { ...it, [field]: value } : it
      );
      return { ...es, items };
    });
  };

  const editRemoveItem = (idx) => {
    setEditSale((es) => ({
      ...es,
      items: es.items.filter((_, i) => i !== idx),
    }));
  };

  const editAddManualItem = () => {
    setEditSale((es) => ({
      ...es,
      items: [
        ...es.items,
        {
          product_id: null,
          product_code: "MANUAL",
          name: "New item",
          quantity: 1,
          rate: 0,
          discount: 0,
          amount: 0,
        },
      ],
    }));
  };

  const editComputedTotals = editSale
    ? (() => {
        const sub = (editSale.items || []).reduce(
          (a, it) =>
            a +
            (Number(it.quantity) || 0) * (Number(it.rate) || 0) -
            (Number(it.discount) || 0),
          0
        );
        const grand = Math.max(0, sub - (Number(editSale.overall_discount) || 0));
        return { sub, grand };
      })()
    : { sub: 0, grand: 0 };

  const saveEdit = async () => {
    if (!editSale) return;
    setEditBusy(true);
    try {
      const payload = {
        customer_type: editSale.customer_type || "walkin",
        party_id: editSale.party_id || null,
        customer_name: editSale.customer_name || "Walk-in Customer",
        items: editSale.items.map((it) => ({
          product_id: it.product_id || null,
          product_code: it.product_code || "",
          name: it.name,
          quantity: Number(it.quantity) || 0,
          rate: Number(it.rate) || 0,
          discount: Number(it.discount) || 0,
          amount:
            (Number(it.quantity) || 0) * (Number(it.rate) || 0) -
            (Number(it.discount) || 0),
        })),
        overall_discount: Number(editSale.overall_discount) || 0,
        payment_method: editSale.payment_method || "cash",
        notes: editSale.notes || "",
      };
      await api.put(`/sales/${editSale.id}`, payload);
      toast.success(`Invoice ${editSale.invoice_no} updated`);
      setEditSale(null);
      loadSales();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setEditBusy(false);
    }
  };

  return (
    <>
      <PageShell
        ticker={[
          { label: "Console", value: "REPORTS", live: true },
          { label: "View", value: tab.toUpperCase() },
          { label: "Range", value: (tab === "profit" ? profitRange : range).toUpperCase() },
          { label: "Currency", value: "PKR ₨" },
        ]}
        eyebrow="Chapter · Books"
        title={<>The numbers, <em className="italic text-blue-700">laid out</em></>}
        description="Daily, weekly, monthly. Sales, profit, inventory, ledgers — export any of it."
        colophon="Reports"
      >
        <div className="no-print" data-testid="reports-page">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-white border border-slate-200" data-testid="reports-tabs">
            <TabsTrigger value="sales" data-testid="tab-sales">Sales</TabsTrigger>
            <TabsTrigger value="profit" data-testid="tab-profit">Profit</TabsTrigger>
            <TabsTrigger value="inventory" data-testid="tab-inventory">Inventory</TabsTrigger>
            <TabsTrigger value="lowstock" data-testid="tab-lowstock">Low Stock</TabsTrigger>
          </TabsList>

          {/* SALES */}
          <TabsContent value="sales" className="mt-4">
            <Card className="bg-white border border-slate-200 p-4 mb-4">
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <Label className="text-xs">Range</Label>
                  <Select value={range} onValueChange={setRange}>
                    <SelectTrigger className="w-40" data-testid="sales-range-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Today</SelectItem>
                      <SelectItem value="weekly">This Week</SelectItem>
                      <SelectItem value="monthly">This Month</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {range === "custom" && (
                  <>
                    <div>
                      <Label className="text-xs">From</Label>
                      <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="sales-from" />
                    </div>
                    <div>
                      <Label className="text-xs">To</Label>
                      <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="sales-to" />
                    </div>
                  </>
                )}
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const params = new URLSearchParams(
                        range === "custom"
                          ? { range_type: "custom", date_from: from, date_to: to }
                          : { range_type: range }
                      );
                      await downloadFile(`/export/sales?${params.toString()}`, `sales_${range}.xlsx`);
                    }}
                    data-testid="sales-export-btn"
                  >
                    <Download size={16} className="mr-2" /> Excel
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="p-3 border border-slate-200 rounded">
                  <div className="text-xs uppercase text-slate-500">Total Invoices</div>
                  <div className="font-heading text-xl font-bold tabular">{salesData.count}</div>
                </div>
                <div className="p-3 border border-slate-200 rounded">
                  <div className="text-xs uppercase text-slate-500">Total Sales</div>
                  <div className="font-heading text-xl font-bold tabular text-blue-700">
                    {fmtPKR(salesData.total)}
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 text-left">Invoice</th>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-left">Payment</th>
                    <th className="px-4 py-3 text-right">Items</th>
                    <th className="px-4 py-3 text-right">Discount</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {salesData.sales.map((s) => (
                    <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sales-row-${s.invoice_no}`}>
                      <td className="px-4 py-2 font-mono-tabular text-xs">{s.invoice_no}</td>
                      <td className="px-4 py-2 text-slate-500 text-xs">{fmtDateTime(s.date)}</td>
                      <td className="px-4 py-2 text-slate-900">{s.customer_name}</td>
                      <td className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500">
                        {s.payment_method}
                      </td>
                      <td className="px-4 py-2 text-right tabular">{s.items?.length || 0}</td>
                      <td className="px-4 py-2 text-right tabular">{fmtPKR(s.overall_discount || 0)}</td>
                      <td className="px-4 py-2 text-right tabular font-semibold">{fmtPKR(s.grand_total)}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => openEdit(s)}
                          className="text-slate-500 hover:text-blue-700 mr-2"
                          data-testid={`sales-edit-${s.invoice_no}`}
                          title="Edit invoice"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => reprint(s)}
                          className="text-slate-500 hover:text-blue-700 mr-2"
                          data-testid={`sales-print-${s.invoice_no}`}
                          title="Print"
                        >
                          <Printer size={14} />
                        </button>
                        <button
                          onClick={() => downloadPdf(s)}
                          className="text-slate-500 hover:text-blue-700 mr-2"
                          data-testid={`sales-pdf-${s.invoice_no}`}
                          title="Download PDF"
                        >
                          <FileDown size={14} />
                        </button>
                        <button
                          onClick={() => shareWa(s)}
                          className="text-slate-500 hover:text-emerald-700 mr-2"
                          data-testid={`sales-wa-${s.invoice_no}`}
                          title="Share on WhatsApp"
                        >
                          <Share2 size={14} />
                        </button>
                        <button
                          onClick={() => deleteSale(s)}
                          className="text-slate-500 hover:text-red-700"
                          data-testid={`sales-delete-${s.invoice_no}`}
                          title="Delete & restore stock"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {salesData.sales.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-slate-400 text-sm">
                        No sales in this range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          {/* PROFIT */}
          <TabsContent value="profit" className="mt-4">
            <Card className="bg-white border border-slate-200 p-4 mb-4">
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <Label className="text-xs">Range</Label>
                  <Select value={profitRange} onValueChange={setProfitRange}>
                    <SelectTrigger className="w-40" data-testid="profit-range-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Today</SelectItem>
                      <SelectItem value="weekly">This Week</SelectItem>
                      <SelectItem value="monthly">This Month</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {profitRange === "custom" && (
                  <>
                    <div>
                      <Label className="text-xs">From</Label>
                      <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">To</Label>
                      <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                    </div>
                  </>
                )}
                <div className="ml-auto">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const params = new URLSearchParams(
                        profitRange === "custom"
                          ? { range_type: "custom", date_from: from, date_to: to }
                          : { range_type: profitRange }
                      );
                      await downloadFile(`/export/profit?${params.toString()}`, `profit_${profitRange}.xlsx`);
                    }}
                    data-testid="profit-export-btn"
                  >
                    <Download size={16} className="mr-2" /> Excel
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="p-3 border border-slate-200 rounded">
                  <div className="text-xs uppercase text-slate-500">Revenue</div>
                  <div className="font-heading text-xl font-bold tabular">{fmtPKR(profitData.total_revenue)}</div>
                </div>
                <div className="p-3 border border-slate-200 rounded">
                  <div className="text-xs uppercase text-slate-500">Cost</div>
                  <div className="font-heading text-xl font-bold tabular text-slate-700">{fmtPKR(profitData.total_cost)}</div>
                </div>
                <div className="p-3 border border-slate-200 rounded">
                  <div className="text-xs uppercase text-slate-500">Profit</div>
                  <div className={`font-heading text-xl font-bold tabular ${profitData.total_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {fmtPKR(profitData.total_profit)}
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 text-left">Invoice</th>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-right">Revenue</th>
                    <th className="px-4 py-3 text-right">Cost</th>
                    <th className="px-4 py-3 text-right">Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {profitData.rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono-tabular text-xs">{r.invoice_no}</td>
                      <td className="px-4 py-2 text-slate-500 text-xs">{fmtDateTime(r.date)}</td>
                      <td className="px-4 py-2">{r.customer}</td>
                      <td className="px-4 py-2 text-right tabular">{fmtPKR(r.revenue)}</td>
                      <td className="px-4 py-2 text-right tabular text-slate-700">{fmtPKR(r.cost)}</td>
                      <td className={`px-4 py-2 text-right tabular font-semibold ${r.profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                        {fmtPKR(r.profit)}
                      </td>
                    </tr>
                  ))}
                  {profitData.rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-slate-400 text-sm">
                        No sales in this range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          {/* INVENTORY */}
          <TabsContent value="inventory" className="mt-4">
            <Card className="bg-white border border-slate-200 p-4 mb-4">
              <div className="flex items-end justify-between flex-wrap gap-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
                  <div className="p-3 border border-slate-200 rounded">
                    <div className="text-xs uppercase text-slate-500">Total Items</div>
                    <div className="font-heading text-lg font-bold tabular">{invData?.total_items ?? 0}</div>
                  </div>
                  <div className="p-3 border border-slate-200 rounded">
                    <div className="text-xs uppercase text-slate-500">Stock Units</div>
                    <div className="font-heading text-lg font-bold tabular">{invData?.total_stock_units ?? 0}</div>
                  </div>
                  <div className="p-3 border border-slate-200 rounded">
                    <div className="text-xs uppercase text-slate-500">Value (Cost)</div>
                    <div className="font-heading text-lg font-bold tabular">{fmtPKR(invData?.total_value_cost)}</div>
                  </div>
                  <div className="p-3 border border-slate-200 rounded">
                    <div className="text-xs uppercase text-slate-500">Value (Sale)</div>
                    <div className="font-heading text-lg font-bold tabular text-blue-700">{fmtPKR(invData?.total_value_sale)}</div>
                  </div>
                </div>
                <Button variant="outline" onClick={() => downloadFile("/export/inventory", "inventory.xlsx")} data-testid="inv-report-export">
                  <Download size={16} className="mr-2" /> Excel
                </Button>
              </div>
            </Card>
            <Card className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 text-left">Code</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Brand</th>
                    <th className="px-4 py-3 text-right">Stock</th>
                    <th className="px-4 py-3 text-right">Cost</th>
                    <th className="px-4 py-3 text-right">Price</th>
                    <th className="px-4 py-3 text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {(invData?.products || []).map((p) => (
                    <tr key={p.id} className={`border-t border-slate-100 hover:bg-slate-50 ${p.current_stock <= p.minimum_stock ? "row-low-stock" : ""}`}>
                      <td className="px-4 py-2 font-mono-tabular text-xs">{p.product_code}</td>
                      <td className="px-4 py-2">{p.name}</td>
                      <td className="px-4 py-2 text-slate-500">{p.brand || "-"}</td>
                      <td className="px-4 py-2 text-right tabular">{p.current_stock}</td>
                      <td className="px-4 py-2 text-right tabular">{fmtPKR(p.purchase_cost)}</td>
                      <td className="px-4 py-2 text-right tabular">{fmtPKR(p.sale_price)}</td>
                      <td className="px-4 py-2 text-right tabular font-semibold">{fmtPKR((p.current_stock || 0) * (p.purchase_cost || 0))}</td>
                    </tr>
                  ))}
                  {(invData?.products || []).length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">No products.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          {/* LOW STOCK */}
          <TabsContent value="lowstock" className="mt-4">
            <Card className="bg-white border border-slate-200 p-4 mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase text-slate-500">Items at/under minimum stock</div>
                <div className="font-heading text-xl font-bold tabular text-red-700">{lowData?.count ?? 0}</div>
              </div>
              <Button variant="outline" onClick={() => downloadFile("/export/low-stock", "low_stock.xlsx")} data-testid="low-export-btn">
                <Download size={16} className="mr-2" /> Excel
              </Button>
            </Card>
            <Card className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 text-left">Code</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Brand</th>
                    <th className="px-4 py-3 text-right">Current</th>
                    <th className="px-4 py-3 text-right">Minimum</th>
                  </tr>
                </thead>
                <tbody>
                  {(lowData?.products || []).map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 row-low-stock">
                      <td className="px-4 py-2 font-mono-tabular text-xs">{p.product_code}</td>
                      <td className="px-4 py-2">{p.name}</td>
                      <td className="px-4 py-2 text-slate-500">{p.brand || "-"}</td>
                      <td className="px-4 py-2 text-right tabular text-red-700 font-semibold">{p.current_stock}</td>
                      <td className="px-4 py-2 text-right tabular text-slate-500">{p.minimum_stock}</td>
                    </tr>
                  ))}
                  {(lowData?.products || []).length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400 text-sm">All stock healthy.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        </Tabs>
        </div>
      </PageShell>

      {/* ---- Edit Invoice Dialog ---- */}
      <Dialog open={!!editSale} onOpenChange={(o) => !o && setEditSale(null)}>
        <DialogContent className="max-w-3xl" data-testid="edit-invoice-dialog">
          <DialogHeader>
            <DialogTitle>
              Edit Invoice{" "}
              <span className="font-mono-tabular text-blue-700">
                {editSale?.invoice_no}
              </span>
            </DialogTitle>
          </DialogHeader>
          {editSale && (
            <div className="space-y-4">
              {/* Customer + Payment row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Customer Name
                  </Label>
                  <Input
                    value={editSale.customer_name || ""}
                    onChange={(e) =>
                      setEditSale({ ...editSale, customer_name: e.target.value })
                    }
                    disabled={editSale.customer_type === "existing"}
                    data-testid="edit-customer-name"
                  />
                  {editSale.customer_type === "existing" && (
                    <p className="text-[10px] text-slate-400 mt-1">
                      Existing customer — name comes from the party record.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Payment Method
                  </Label>
                  <Select
                    value={editSale.payment_method || "cash"}
                    onValueChange={(v) =>
                      setEditSale({ ...editSale, payment_method: v })
                    }
                  >
                    <SelectTrigger data-testid="edit-payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem
                        value="ledger"
                        disabled={!editSale.party_id}
                      >
                        On Ledger
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Items
                  </Label>
                  <button
                    onClick={editAddManualItem}
                    className="text-xs uppercase tracking-wider text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                    data-testid="edit-add-item-btn"
                  >
                    <Plus size={12} /> Add manual line
                  </button>
                </div>
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[620px]">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-2 text-left">Code</th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-right w-20">Qty</th>
                          <th className="px-3 py-2 text-right w-28">Rate</th>
                          <th className="px-3 py-2 text-right w-28">Disc</th>
                          <th className="px-3 py-2 text-right w-28">Amount</th>
                          <th className="px-3 py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {editSale.items.map((it, idx) => {
                          const amt =
                            (Number(it.quantity) || 0) * (Number(it.rate) || 0) -
                            (Number(it.discount) || 0);
                          return (
                            <tr
                              key={idx}
                              className="border-t border-slate-100"
                              data-testid={`edit-item-${idx}`}
                            >
                              <td className="px-3 py-2 font-mono-tabular text-xs text-slate-500">
                                {it.product_code || "-"}
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  value={it.name}
                                  onChange={(e) =>
                                    editUpdateItem(idx, "name", e.target.value)
                                  }
                                  className="h-8 text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  type="number"
                                  min="0"
                                  value={it.quantity}
                                  onChange={(e) =>
                                    editUpdateItem(idx, "quantity", e.target.value)
                                  }
                                  className="h-8 text-right tabular"
                                  data-testid={`edit-qty-${idx}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  type="number"
                                  min="0"
                                  value={it.rate}
                                  onChange={(e) =>
                                    editUpdateItem(idx, "rate", e.target.value)
                                  }
                                  className="h-8 text-right tabular"
                                  data-testid={`edit-rate-${idx}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  type="number"
                                  min="0"
                                  value={it.discount || 0}
                                  onChange={(e) =>
                                    editUpdateItem(idx, "discount", e.target.value)
                                  }
                                  className="h-8 text-right tabular"
                                />
                              </td>
                              <td className="px-3 py-2 text-right tabular font-semibold">
                                {fmtPKR(amt)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  onClick={() => editRemoveItem(idx)}
                                  className="text-slate-400 hover:text-red-600"
                                  data-testid={`edit-remove-${idx}`}
                                  title="Remove"
                                >
                                  <X size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Totals + Discount + Notes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Overall Discount (₨)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={editSale.overall_discount || 0}
                    onChange={(e) =>
                      setEditSale({
                        ...editSale,
                        overall_discount: e.target.value,
                      })
                    }
                    data-testid="edit-overall-discount"
                  />
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Subtotal</div>
                  <div className="tabular text-slate-700">
                    {fmtPKR(editComputedTotals.sub)}
                  </div>
                  <div className="text-xs text-slate-500 mt-2">New Grand Total</div>
                  <div className="font-serif text-2xl tabular text-slate-900" data-testid="edit-grand-total">
                    {fmtPKR(editComputedTotals.grand)}
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Notes
                </Label>
                <Input
                  value={editSale.notes || ""}
                  onChange={(e) =>
                    setEditSale({ ...editSale, notes: e.target.value })
                  }
                  placeholder="Optional notes for the invoice"
                />
              </div>

              <p className="text-[11px] text-slate-500 leading-relaxed">
                Editing will restore stock from the original items and re-apply the new ones.
                Ledger entries are refreshed automatically.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSale(null)}>
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              disabled={editBusy || !editSale?.items?.length}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="edit-save-btn"
            >
              {editBusy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note: printing & WhatsApp share open the server-generated PDF in a clean popup, no in-page print iframe needed */}
    </>
  );
}
